import { cmsQueryParamsPrefix } from '../../utils/routing';
import { getSortOptions } from './sortOptions';
import { ProductSortConfig, ProductSortOption } from './types';

/** Composed from the prefix that owns it, like the sibling PL params (R10.2.4). */
export const SORT_QUERY_PARAM = `${cmsQueryParamsPrefix}sort`;

/**
 * A gated option is absent rather than hidden, so an old ad link cannot activate
 * a measure the market does not offer (R13.2, R13.3).
 */
export const getVisibleSortOptions = getSortOptions;

/**
 * The editor's choice, matched on the Strapi value, falling back to the first
 * authored option — which covers a default naming a measure this page does not
 * render, such as SG's `EIR` on a HK page.
 *
 * `default_sort_option` names a measure, not a direction, so a measure authored
 * in both directions resolves to the ascending one — the suffixed slug has no
 * spelling in that field.
 */
export const resolveDefaultSortOption = (
  config: ProductSortConfig | undefined,
): ProductSortOption | undefined => {
  const options = getSortOptions(config);
  if (!options.length) return undefined;

  const named = config?.defaultSort
    ? options.filter((option) => option.sourceValue === config.defaultSort)
    : [];

  return (
    named.find((option) => !option.value.endsWith('-desc')) ??
    named[0] ??
    options[0]
  );
};

/**
 * An absent or unrecognised parameter falls back to the editor's default rather
 * than to Popular outright as AC 4c reads — that would make a configured
 * default unreachable from a mistyped URL. There is no built-in default to fall
 * back to either: the chain is the named measure, then the first option that
 * names it, then the first authored option.
 */
export const resolveActiveSortOption = (
  config: ProductSortConfig | undefined,
  paramValue: string | null | undefined,
): ProductSortOption | undefined => {
  const options = getSortOptions(config);
  if (!options.length) return undefined;

  const fromUrl = paramValue
    ? options.find((option) => option.value === paramValue)
    : undefined;

  return fromUrl ?? resolveDefaultSortOption(config);
};
